import { Unplug } from 'lucide-react'

import type { ReactElement } from 'react'

import { Card } from '@/components/ui/card'
import { Explicacao } from '@/features/relatorio/components/explicacao'
import { Fonte } from '@/features/relatorio/components/fonte'
import { cn } from '@/lib/cn'

export interface IndicadorPendenteProps {
  rotulo: string
  /** Onde o dado precisaria vir de (ex.: "Forms / MemberHub"). */
  fonteEsperada: string
  /** Por que ele ainda não existe — o usuário precisa saber o que destrava. */
  motivo: string
  cadencia?: string
  /** Dado parcial que já existe hoje, quando houver (ex.: a posição atual). */
  detalhe?: string
  tamanho?: 'normal' | 'telao'
}

/**
 * Indicador previsto pelo relatório cuja fonte ainda não existe. Mostra o lugar
 * dele no painel com um traço no lugar do número — inventar um valor ou omitir o
 * card faria o bloco parecer completo quando não está.
 */
export function IndicadorPendente({
  rotulo,
  fonteEsperada,
  motivo,
  cadencia,
  detalhe,
  tamanho = 'normal',
}: IndicadorPendenteProps): ReactElement {
  const telao = tamanho === 'telao'
  return (
    <Card className={cn('border-dashed flex flex-col gap-3', telao ? 'p-6 sm:p-7' : 'p-4 sm:p-5')}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-1">
          <span
            className={cn(
              'text-muted-foreground truncate font-semibold uppercase tracking-[0.14em]',
              telao ? 'text-sm' : 'text-[11px]',
            )}
          >
            {rotulo}
          </span>
          <Explicacao
            rotulo={rotulo}
            oQueE={`Indicador previsto no relatório, ainda sem fonte de dado conectada.`}
            formula="—"
            origem={fonteEsperada}
            regra={motivo}
          />
        </div>
        {cadencia ? (
          <span className="border-border text-muted-foreground shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide">
            {cadencia}
          </span>
        ) : null}
      </div>

      <div className="flex flex-wrap items-baseline gap-2">
        <span
          className={cn(
            'text-muted-foreground/60 font-mono font-semibold leading-none',
            telao ? 'text-5xl sm:text-6xl' : 'text-3xl sm:text-[2.5rem]',
          )}
        >
          —
        </span>
        {detalhe ? <span className="text-muted-foreground text-xs">{detalhe}</span> : null}
      </div>

      <p className={cn('text-muted-foreground flex items-start gap-1.5', telao ? 'text-sm' : 'text-xs')}>
        <Unplug className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          Aguardando fonte: <span className="text-foreground font-medium">{fonteEsperada}</span>
        </span>
      </p>

      <div className="mt-auto flex justify-end">
        <Fonte origem="pendente" campo={fonteEsperada} />
      </div>
    </Card>
  )
}
